import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import PeriodNavigator from '../components/PeriodNavigator'
import { todayIso } from '../format'
import { ACTIVITY_TYPE_COLORS, advancedColors } from '../theme'
import { ActivityBlock } from '../activityBlock'
import { usePatientActivity } from '../api/patientActivity'
import { MonthDay, monthGridFor, monthLabel } from './weekDates'

const VISIBLE_PER_DAY = 3
const WEEKDAY_HEADERS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const dayNumberColorFor = (isToday: boolean, dimmed: boolean): string => {
  if (isToday) {
    return advancedColors.surface
  }
  if (dimmed) {
    return advancedColors.inkFaint
  }
  return advancedColors.ink
}

const MonthDayCell = ({ day, isToday, blocks }: { day: MonthDay; isToday: boolean; blocks: ActivityBlock[] }) => {
  const visible = blocks.slice(0, VISIBLE_PER_DAY)
  const overflow = blocks.length - visible.length
  const dimmed = !day.inCurrentMonth

  const dayNumberColor = dayNumberColorFor(isToday, dimmed)

  return (
    <Box
      sx={{
        border: `1px solid ${advancedColors.line}`,
        borderRadius: 1,
        p: 0.75,
        minHeight: 92,
        minWidth: 0,
        backgroundColor: dimmed ? advancedColors.canvas : advancedColors.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
          width: 20,
          height: 20,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isToday ? advancedColors.green : 'transparent',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: isToday ? 700 : 500, color: dayNumberColor }}>
          {day.dayOfMonth}
        </Typography>
      </Box>

      {visible.map((block) => (
        <Box key={block.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: ACTIVITY_TYPE_COLORS[block.type],
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            noWrap
            sx={{ flex: 1, minWidth: 0, color: dimmed ? advancedColors.inkFaint : advancedColors.ink }}
          >
            {block.title}
          </Typography>
        </Box>
      ))}

      {overflow > 0 && (
        <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
          +{overflow} more
        </Typography>
      )}
    </Box>
  )
}

const MonthlyActivityBoard = () => {
  const [monthOffset, setMonthOffset] = useState(0)
  const today = todayIso()
  const days = monthGridFor(today, monthOffset)
  const weekCount = days.length / 7
  const activity = usePatientActivity()

  return (
    <Box>
      <PeriodNavigator
        label={monthLabel(today, monthOffset)}
        unit="month"
        onStep={(delta) => setMonthOffset((value) => value + delta)}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5, mb: 0.5 }}>
        {WEEKDAY_HEADERS.map((label) => (
          <Typography key={label} variant="overline" sx={{ textAlign: 'center' }}>
            {label}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridTemplateRows: `repeat(${weekCount}, 1fr)`,
          gap: 0.5,
        }}
      >
        {days.map((day) => (
          <MonthDayCell key={day.iso} day={day} isToday={day.iso === today} blocks={activity.get(day.iso) ?? []} />
        ))}
      </Box>
    </Box>
  )
}

export default MonthlyActivityBoard
